import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import API from "./api";
import { isAppWebView } from "../utils/appWebView";

let configPromise;
let appInstance;
let listenerBound = false;
let registrationPromise;
let foregroundNotificationAudio;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function registerNativePushToken(userId) {
  const numericUserId = Number(userId || 0);
  if (!numericUserId) return false;

  for (let attempt = 0; attempt < 10; attempt += 1) {
    try {
      if (window.POSMApp?.postMessage) {
        window.POSMApp.postMessage(JSON.stringify({ type: "registerPush", userId: numericUserId }));
        return true;
      }
      if (window.__mkRegisterNativePush?.(numericUserId)) return true;
    } catch {
      // Try again below; the native bridge can be a little late after page load.
    }
    await sleep(250);
  }

  return false;
}

function playForegroundNotificationSound() {
  if (!foregroundNotificationAudio) {
    foregroundNotificationAudio = new Audio("/sounds/user-notification.mp3");
    foregroundNotificationAudio.preload = "auto";
    foregroundNotificationAudio.volume = 0.86;
  }
  foregroundNotificationAudio.currentTime = 0;
  foregroundNotificationAudio.play().catch(() => {
    // Browser may block sound until the user interacts with the page.
  });
}

async function loadPushConfig() {
  if (!configPromise) {
    configPromise = API.get("/push/config")
      .then((res) => res.data || { enabled: false })
      .catch(() => ({ enabled: false }));
  }
  return configPromise;
}

async function getFirebaseApp() {
  const config = await loadPushConfig();
  if (!config.enabled) return null;

  if (!appInstance) {
    appInstance = getApps()[0] || initializeApp(config.firebase);
  }

  return { app: appInstance, config };
}

async function registerPushToken(userId) {
  if (isAppWebView()) {
    return registerNativePushToken(userId);
  }

  if (!userId || !("Notification" in window) || !("serviceWorker" in navigator)) return false;
  if (!(await isSupported())) return;

  // Check if we're in an iframe - service workers cannot be registered from iframes
  if (window.self !== window.top) return false;

  const bundle = await getFirebaseApp();
  if (!bundle || Notification.permission !== "granted") return false;

  if (!registrationPromise) {
    try {
      registrationPromise = navigator.serviceWorker.register("/firebase-messaging-sw.js").catch((error) => {
        console.error("Failed to register service worker:", error);
        registrationPromise = null;
        return null;
      });
    } catch (error) {
      console.error("Failed to register service worker:", error);
      return false;
    }
  }

  const registration = await registrationPromise;
  if (!registration) return false;

  const messaging = getMessaging(bundle.app);
  const token = await getToken(messaging, {
    vapidKey: bundle.config.vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) return false;

  await API.post("/push/register", {
    user_id: userId,
    token,
    device_type: "web",
  });

  if (!listenerBound) {
    onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || payload?.data?.title || "POS Matka";
      const body = payload?.notification?.body || payload?.data?.body || "";
      playForegroundNotificationSound();
      if (Notification.permission === "granted") {
        new Notification(title, { body, icon: "/logo5.png", badge: "/logo5.png" });
      }
    });
    listenerBound = true;
  }

  return true;
}

export async function initPushNotifications(userId) {
  if (isAppWebView()) return registerPushToken(userId);
  if (!userId || !("Notification" in window) || Notification.permission !== "granted") return false;
  return registerPushToken(userId);
}

export async function requestPushNotifications(userId) {
  if (isAppWebView()) return registerPushToken(userId);
  if (!userId || !("Notification" in window)) return false;

  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();

  if (permission !== "granted") return false;
  return registerPushToken(userId);
}
